import { useState } from "react";
import { Phone, StickyNote, Star, FileCheck, FileWarning, Settings2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { STATUSES, statusColors, reviewSmsHref, termsRequestSmsHref, fmtDate } from "./leadUtils";

const LeadMessage = ({ lead }) => {
  const [expanded, setExpanded] = useState(false);
  if (!lead.message) return null;
  const long = lead.message.length > 120;
  return (
    <div>
      <p data-testid={`lead-message-${lead.id}`} className={`text-zinc-400 text-xs leading-relaxed whitespace-pre-wrap ${expanded || !long ? "" : "line-clamp-2"}`}>{lead.message}</p>
      {long && (
        <button data-testid={`lead-message-toggle-${lead.id}`} onClick={() => setExpanded((e) => !e)} className="text-blue-400 hover:text-blue-300 text-[11px] font-semibold mt-0.5">
          {expanded ? "Show less" : "Show full message"}
        </button>
      )}
    </div>
  );
};

export const LeadRow = ({ lead, onStatusChange, onOpenNotes, onManage }) => (
  <div
    data-testid={`lead-row-${lead.id}`}
    className="bg-[#121212] border border-zinc-800 rounded-xl p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start lg:items-center"
  >
    <div className="lg:col-span-3">
      <div className="font-heading font-semibold text-white flex items-center gap-2 flex-wrap">
        {lead.name}
        {lead.source && lead.source !== "Website Form" && (
          <Badge variant="outline" className="bg-emerald-600/15 text-emerald-400 border-emerald-500/40 rounded-sm text-[10px]" data-testid={`lead-source-${lead.id}`}>
            {lead.source}
          </Badge>
        )}
      </div>
      <a
        data-testid={`lead-phone-${lead.id}`}
        href={`tel:${lead.phone.replace(/[^+\d]/g, "")}`}
        className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm mt-0.5"
      >
        <Phone className="h-3.5 w-3.5" /> {lead.phone}
      </a>
      {lead.email && <div className="text-zinc-500 text-xs mt-0.5 break-all">{lead.email}</div>}
    </div>

    <div className="lg:col-span-3">
      <div className="text-white text-sm">{lead.vehicle_year} {lead.vehicle_make} {lead.vehicle_model}</div>
      <div className="text-zinc-400 text-sm">{lead.service_needed}</div>
      {lead.preferred_date && <div className="text-zinc-500 text-xs mt-0.5">Preferred: {lead.preferred_date}</div>}
    </div>

    <div className="lg:col-span-3">
      {lead.message && <LeadMessage lead={lead} />}
      <div className="text-zinc-600 text-xs mt-1">{fmtDate(lead.created_at)}</div>
      {lead.terms_accepted ? (
        <div className="inline-flex items-center gap-1 text-emerald-400 text-[11px] mt-1" data-testid={`lead-terms-accepted-${lead.id}`}>
          <FileCheck className="h-3 w-3" /> Terms accepted {lead.terms_accepted_at ? fmtDate(lead.terms_accepted_at) : ""} (v{lead.terms_version || "1.0"})
        </div>
      ) : (
        <div className="inline-flex items-center gap-1 text-amber-400 text-[11px] mt-1" data-testid={`lead-terms-pending-${lead.id}`}>
          <FileWarning className="h-3 w-3" /> Terms not accepted
        </div>
      )}
    </div>

    <div className="lg:col-span-3 flex items-center gap-2 flex-wrap lg:justify-end">
      {!lead.terms_accepted && (
        <a
          data-testid={`terms-request-${lead.id}`}
          href={termsRequestSmsHref(lead)}
          className="inline-flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/40 text-blue-400 hover:bg-blue-500/20 rounded-sm px-3 h-9 text-xs font-semibold transition-colors"
        >
          <FileCheck className="h-3.5 w-3.5" /> Request Terms
        </a>
      )}
      {lead.status === "Completed" && (
        <a
          data-testid={`review-request-${lead.id}`}
          href={reviewSmsHref(lead)}
          className="inline-flex items-center gap-1.5 bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25 rounded-sm px-3 h-9 text-xs font-semibold transition-colors"
        >
          <Star className="h-3.5 w-3.5" /> Text Review Link
        </a>
      )}
      <Badge variant="outline" className={`${statusColors[lead.status]} rounded-sm`}>{lead.status}</Badge>
      <Select value={lead.status} onValueChange={(v) => onStatusChange(lead.id, v)}>
        <SelectTrigger data-testid={`lead-status-select-${lead.id}`} className="bg-[#0a0a0a] border-zinc-800 text-white h-9 rounded-sm w-36 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#1A1A1A] border-zinc-800 text-white">
          {STATUSES.map((s) => (
            <SelectItem key={s} value={s} className="focus:bg-blue-600/20 focus:text-white">{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        data-testid={`lead-manage-button-${lead.id}`}
        onClick={() => onManage(lead)}
        className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-3 h-9 text-xs font-semibold transition-colors"
      >
        <Settings2 className="h-3.5 w-3.5" /> Manage
      </button>
      <button
        data-testid={`lead-notes-button-${lead.id}`}
        onClick={() => onOpenNotes(lead)}
        className={`inline-flex items-center gap-1.5 border rounded-sm px-3 h-9 text-xs font-medium transition-colors ${
          lead.notes ? "border-blue-500/50 text-blue-400" : "border-zinc-700 text-zinc-400 hover:text-white"
        }`}
      >
        <StickyNote className="h-3.5 w-3.5" /> Notes
      </button>
    </div>
  </div>
);
